import React, { useState } from 'react';
import { useDevice } from '../../context/DeviceContext';
import './DeviceSleepOverlay.css';

/**
 * 无线设备在驱动界面内休眠/失联时的蒙版提示。
 *
 * 设备掉线不再把用户踢回设备选择页：驱动界面与已读配置原样保留在蒙版之下，
 * 设备被唤醒后自动回读数据并关闭蒙版；用户也可以主动返回设备列表。
 */
const SLEEP_TITLES = {
  offline: '设备已进入休眠',
  unpaired: '接收器尚未配对设备',
  'mcu-error': '接收器通信异常',
  timeout: '设备长时间无响应',
};

function DeviceSleepOverlay() {
  const {
    deviceSuspended,
    suspendNotice,
    retrySuspendedDevice,
    disconnectDevice,
    deviceInfo,
  } = useDevice();
  const [retrying, setRetrying] = useState(false);

  if (!deviceSuspended) return null;

  const statusKey = suspendNotice?.statusKey || 'offline';
  const title = SLEEP_TITLES[statusKey] || '设备已离线';
  const resuming = Boolean(suspendNotice?.resuming);
  const resumeError = suspendNotice?.resumeError || null;
  const description =
    statusKey === 'offline'
      ? '无线设备已进入休眠或长时间没有信号输入，当前配置界面已暂停操作。'
      : suspendNotice?.message ||
        '无线设备当前不可用，当前配置界面已暂停操作。';

  const handleRetry = async () => {
    if (retrying || resuming) return;
    setRetrying(true);
    try {
      await retrySuspendedDevice?.();
    } finally {
      setRetrying(false);
    }
  };

  return (
    <div className="device-sleep-backdrop">
      <section
        className="device-sleep-dialog"
        role="alertdialog"
        aria-modal="true"
        aria-labelledby="device-sleep-title"
        aria-describedby="device-sleep-description"
      >
        <div className="device-sleep-header">
          <span className="device-sleep-eyebrow">设备状态</span>
          <h2 id="device-sleep-title">{title}</h2>
          {deviceInfo?.productName && (
            <p className="device-sleep-device-name">{deviceInfo.productName}</p>
          )}
        </div>

        <p id="device-sleep-description" className="device-sleep-description">
          {description}
        </p>

        <div className={`device-sleep-status ${resuming ? 'resuming' : ''}`}>
          <span className="device-sleep-spinner" aria-hidden="true" />
          <span>
            {resuming
              ? '设备已唤醒，正在重新读取设备配置…'
              : '正在等待设备重新连接…'}
          </span>
        </div>

        <ul className="device-sleep-hints">
          <li>移动鼠标或按下任意按键即可唤醒设备，唤醒后界面会自动恢复。</li>
          <li>若长时间没有恢复，请确认设备已开机、电量充足并处于 2.4G 模式。</li>
        </ul>

        {resumeError && (
          <p className="device-sleep-error" role="alert">
            {resumeError}
          </p>
        )}

        <div className="device-sleep-actions">
          <button
            type="button"
            className="device-sleep-button secondary"
            onClick={disconnectDevice}
          >
            返回设备列表
          </button>
          <button
            type="button"
            className="device-sleep-button primary"
            onClick={handleRetry}
            disabled={retrying || resuming}
          >
            {retrying || resuming ? '检测中…' : '立即重试'}
          </button>
        </div>
      </section>
    </div>
  );
}

export default DeviceSleepOverlay;
